import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { Status, ThreeDotMenu } from '@mm/components';
import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import { selectGoogleAccounts } from '@mm/state/selectors/googleSheetSelectors';
import { colors } from '@mm/utils';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import {
  activeIntegrationSelector,
  orderedConnectionsSelector,
  selectGoogleSheetsConnectionsByAccountId,
} from '@mm/state/modules/connectionsTableState/selectors';
import {
  GOOGLE_SHEET,
  useConnectionsTableActions,
} from '@mm/state/modules/connectionsTableState/actions';
import { GoogleSheets } from './GoogleSheets';
import {
  ComponentDataItem,
  renderDataCell,
} from '../../../../../../contacts/components/ContactsTable/ContactTable';
import { GridCell } from '../../../../../../../../common/components/Grid/Grid';
import './GoogleSheetsIntegration.scss';
import { EditOutlined } from '@ant-design/icons';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useFetchConnections } from '../../../use-fetch-connections';
import { FormattedConnectionModel } from '@mm/state/model/ConnectionModel';
import { IntegrationSectionProps } from '../../../IntegrationsPanel/IntegrationsPanel';

type CellProps = { item: FormattedConnectionModel };

const renderNameCell = (
  item: FormattedConnectionModel,
  editSheet: (id: number) => void,
) => (
  <GridCell className="name-cell" onClick={() => editSheet(item.id)}>
    {item.name}
  </GridCell>
);

const renderStatusCell = ({ item }: CellProps) => {
  const { statusText, active } = item;
  return (
    <GridCell>
      <Status
        status={statusText}
        color={!active ? colors.grayLight : undefined}
      />
    </GridCell>
  );
};

const renderActionsCell = (
  item: FormattedConnectionModel,
  editSheet: (id: number) => void,
  deleteGoogleSheet: (item: FormattedConnectionModel) => void,
) => {
  const onSelectItem = (menuItem: string) => {
    switch (menuItem) {
      case 'delete':
        return deleteGoogleSheet(item);
      default:
        break;
    }
  };

  const elements = [{ name: 'Delete', value: 'delete' }];

  const onMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <GridCell className="action-cell">
      <EditOutlined
        onClick={() => editSheet(item.id)}
        className="integrations__edit-icon"
      />
      <ThreeDotMenu
        className="integrations__three-dot-menu"
        elements={elements}
        selectItem={onSelectItem}
        onClick={onMenuClick}
      />
    </GridCell>
  );
};

const generateTableCells = (
  editSheet: (id: number) => void,
  deleteGoogleSheet: (item: FormattedConnectionModel) => void,
  onChangeOrder: (orderBy: string) => void,
) => {
  const cells: ComponentDataItem[] = [
    {
      label: 'Name',
      keyName: 'name',
      rowClassName: 'name-cell',
      onChange: onChangeOrder,
      Cell: (props: CellProps) => renderNameCell(props.item, editSheet),
    },
    {
      label: 'Spreadsheet name ',
      keyName: 'googleSpreadsheetTitle',
      onChange: onChangeOrder,
      Cell: renderDataCell('googleSpreadsheetTitle'),
    },
    {
      label: 'Worksheet name',
      keyName: 'googleSheetTitle',
      onChange: onChangeOrder,
      Cell: renderDataCell('googleSheetTitle'),
    },
    {
      label: 'Status',
      keyName: 'statusText',
      onChange: onChangeOrder,
      Cell: renderStatusCell,
    },
    {
      label: 'Created',
      keyName: 'formattedCreatedAt',
      onChange: onChangeOrder,
      Cell: renderDataCell('formattedCreatedAt'),
    },
    {
      label: 'Actions',
      keyName: 'actions',
      Cell: ({ item }: CellProps) =>
        renderActionsCell(item, editSheet, deleteGoogleSheet),
    },
  ];
  return cells;
};

export const GoogleSheetsIntegration = () => {
  const { setConnectionsOrderBy, setActiveIntegration } =
    useConnectionsTableActions();
  const accounts = useSelector(selectGoogleAccounts);
  const activeIntegration = useSelector(activeIntegrationSelector);
  const { getAccounts, disconnectAccount } = useGoogleSheetsCRUDActions();
  const { fetch } = useFetchConnections();
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const connections = useSelector(orderedConnectionsSelector);
  const googleSheetsConnections = useSelector(
    selectGoogleSheetsConnectionsByAccountId,
  );
  const { deleteConnection } = useConnectionCRUDActions();
  const { showSlideoutMenu } = useViewActions();
  const { openDeleteConfirmModal, openGeneralConfirmModal } = useModalActions();

  useEffect(() => {
    getAccounts(facebookPageId);
    fetch(1, 200, 'google_spreadsheets');
  }, []);

  const onDisconnect = (id: number) => {
    const accountEmail = accounts?.find(acc => acc.id === id)?.email;
    openGeneralConfirmModal({
      headerText: `Are you sure you want to disconnect ${accountEmail} account from Google Sheets Integration?`,
      messageText: '',
      onCloseHandler: ok => {
        if (ok) {
          disconnectAccount(id, facebookPageId);
        }
      },
      okButtonText: 'Disconnect',
      okButtonStyling: { red: true },
      cancelButtonText: 'Cancel',
      cancelButtonFirst: true,
      css: {
        primaryButton: 'integrations__diconnect-modal-primary-button',
        secondaryButton: 'integrations__confirm-modal-secondary-button',
      },
    });
  };

  const editGoogleSheet = (id: number) => {
    setActiveIntegrationHandler();
    showSlideoutMenu('googleSheetIntegrationEditor', {
      widgetId: id,
    });
  };
  const deleteGoogleSheet = (item: FormattedConnectionModel) => {
    const deleteCallback = async () => {
      deleteConnection(facebookPageId, item);
    };

    openDeleteConfirmModal({
      headerText: `Are you sure you want to delete ${item.name}?`,
      deleteCallback,
      newStyle: true,
    });
  };
  const onChangeOrder = orderBy => {
    setConnectionsOrderBy(orderBy);
  };
  const setActiveIntegrationHandler = () => {
    if (activeIntegration === GOOGLE_SHEET) return;
    setActiveIntegration(GOOGLE_SHEET);
  };

  const cells = generateTableCells(
    editGoogleSheet,
    deleteGoogleSheet,
    onChangeOrder,
  );

  return (
    <GoogleSheets
      isAccountConnected={!!accounts.length}
      spreadsheetCount={
        connections.filter(c => c.integrationType === 'google_spreadsheet')
          .length
      }
      onDisconnect={onDisconnect}
      cellData={cells}
      googleSheetsConnections={googleSheetsConnections}
      name={GOOGLE_SHEET}
    />
  );
};
